import { useEffect, useRef, useState } from 'react';
import { Link, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import {
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, control, fontFamily, maxContentWidth, price, radii, shadows, space, spacing, type, typeCompact } from '~/ui/tokens';
import { addDaysIso, todayIso } from '../lib/dateUtils';
import { DEFAULT_GUESTS } from '../lib/guests';
import { images } from '../lib/images';
import { mockHotels } from '../lib/api/mockData';
import { useCurrency } from '~/stores/useCurrency';
import { TopNav } from '../components/TopNav';
import { ReferenceSearchBar, type Place, type SearchBarValue } from '../components/ReferenceSearchBar';
import { buildHotelHref, exactHotelMatch, findHotelPlace } from '../lib/destinations';
import { SelectRateCard } from '../components/SelectRateCard';
import { CollectionHotelCard } from '../components/CollectionHotelCard';
import { ComingSoonStrip } from '../components/ComingSoonStrip';
import { Screen } from '../components/Screen';
import { Accordion, Checkbox, SplitHeading } from '../components/ui';
import { SkeletonBlock } from '../components/ui/Skeleton';
import type { DateRange } from '../components/ReferenceDateRangePicker';
import type { HotelSummary } from '../lib/api/types';
import { api } from '~/api';
import { track } from '~/lib/analytics';
import { encodeOccupancy, type Occupancy } from '~/lib/occupancy';
import { getDefaultResidency } from '~/lib/residency';
import { HOMEPAGE_FAQS } from '~/lib/faqs';
import { cmsDestinations, cmsHotels, splitTitle, useHomepageCms, type CmsHotel, type CmsSections } from '~/lib/homepageCms';
import type { ImageSource } from '~/lib/api/types';

const RECENT_SEARCHES = ['Phuket', 'Maldives', 'Hoi An', 'Bangkok'];
type ProductMode = 'hotels' | 'flights' | 'cars';

const PRODUCT_MODES: { id: ProductMode; label: string; searchLabel: string; available: boolean }[] = [
  { id: 'hotels', label: 'Hotels', searchLabel: 'Find your next stay', available: true },
  { id: 'flights', label: 'Flights', searchLabel: 'Find your next flight', available: false },
  { id: 'cars', label: 'Cars', searchLabel: 'Find your next car', available: false },
];
const VISIBLE_PRODUCT_MODES = PRODUCT_MODES.filter((mode) => mode.id !== 'cars');

// Merchandising rails come from the CMS (Admin → CMS). These reference-backed
// demo properties are only shown until the CMS answers, or when it cannot be
// reached, so the page never renders with empty sections.
const FEATURED_OFFERS = mockHotels.slice(0, 7);

const BEST_VALUE_HOTELS = [mockHotels[0], mockHotels[2], mockHotels[6], mockHotels[3], mockHotels[1], mockHotels[4], mockHotels[5]].filter(
  (hotel): hotel is HotelSummary => Boolean(hotel),
);
const LUXURY_HOTELS = [mockHotels[1], mockHotels[3], mockHotels[4], mockHotels[5], mockHotels[0], mockHotels[6], mockHotels[2]].filter(
  (hotel): hotel is HotelSummary => Boolean(hotel),
);

const POPULAR_DESTINATIONS: { key: string; city: string; image: ImageSource | null; place: Place | null }[] = [
  { key: 'phuket', city: 'Phuket', image: images.phuketMarriott, place: null },
  { key: 'maldives', city: 'Maldives', image: images.outriggerMaldives, place: null },
  { key: 'hoi-an', city: 'Hoi An', image: images.wafaifoHoiAn, place: null },
  { key: 'bangkok', city: 'Bangkok', image: images.peninsulaBangkok, place: null },
];

type Heading = { pre: string; accent: string; rest: string; subtitle: string };

// The CMS section's own title/subtitle once it has answered; the designed
// default heading until then.
function sectionHeading(sections: CmsSections, key: keyof CmsSections, fallback: Heading): Heading {
  const section = sections[key];
  if (!section?.title) return fallback;
  return { ...splitTitle(section.title), subtitle: section.subtitle ?? '' };
}

// CMS hotels once the CMS has answered (an emptied section hides its rail);
// the demo properties while it is loading or unreachable.
function sectionHotels(state: ReturnType<typeof useHomepageCms>, key: keyof CmsSections, fallback: HotelSummary[]): Array<HotelSummary | CmsHotel> {
  if (state.status === 'ready') return cmsHotels(state.sections[key]);
  return state.status === 'unavailable' ? fallback : [];
}

const RATE_PILLARS: { icon: keyof typeof Ionicons.glyphMap; title: string; body: string }[] = [
  {
    icon: 'git-network-outline',
    title: 'We compare multiple suppliers',
    body: 'We fetch rates from multiple global hotel suppliers and show you the lowest available price — without adding our commission on top.',
  },
  {
    icon: 'bed-outline',
    title: 'Free membership, no subscription',
    body: 'SuiteScanner+ is free to join and gives you access to book our private hotel rates. No paid membership or recurring fees.',
  },
  {
    icon: 'sparkles-outline',
    title: 'A simpler business model',
    body: 'Instead of relying on large markups, SuiteScanner is supported by a small booking fee. That lets us pass on 100% of the supplier savings back to you.',
  },
];

function defaultDates(): DateRange {
  return { start: addDaysIso(todayIso(), 1), end: addDaysIso(todayIso(), 2) };
}

export default function HomeScreen() {
  useCurrency((state) => state.selectedCurrency);
  const router = useRouter();
  const { width } = useWindowDimensions();
  const heroStacked = width < 900;
  const railInset = heroStacked
    ? spacing(3)
    : Math.max(spacing(3), (width - maxContentWidth) / 2 + spacing(3));
  // The approved desktop card is 15% wider than the previous 312px card.
  // Smaller screens use the available content width without page overflow.
  const offerCardWidth = width >= 1024 ? 359 : Math.min(359, width - spacing(6));
  const railRef = useRef<ScrollView>(null);
  const [railX, setRailX] = useState(0);
  const [railViewportWidth, setRailViewportWidth] = useState(0);
  const [railContentWidth, setRailContentWidth] = useState(0);

  const [search, setSearch] = useState<SearchBarValue>({
    destination: '',
    dates: defaultDates(),
    guests: DEFAULT_GUESTS,
    residency: getDefaultResidency(),
  });
  const [productMode, setProductMode] = useState<ProductMode>('hotels');
  const [freeCancellationOnly, setFreeCancellationOnly] = useState(false);
  const [fourStarsPlus, setFourStarsPlus] = useState(false);
  // Shared across both FAQ columns below so only one question is ever open at
  // a time, instead of each column's Accordion tracking its own index.
  const [openFaqQuestion, setOpenFaqQuestion] = useState<string | null>(null);
  const toggleFaqQuestion = (question: string) => setOpenFaqQuestion((current) => (current === question ? null : question));
  // Position/size of the hot-pink search box within its section, so the blush
  // band can end at exactly the box's vertical middle.
  const [searchBoxBox, setSearchBoxBox] = useState({ y: 0, height: 0 });

  const cms = useHomepageCms();
  const featuredOffers = sectionHotels(cms, 'best_deals', FEATURED_OFFERS);
  const bestValueHotels = sectionHotels(cms, 'best_value_hotels', BEST_VALUE_HOTELS);
  const luxuryHotels = sectionHotels(cms, 'hot_picks', LUXURY_HOTELS);
  const popularDestinations = cms.status === 'ready' ? cmsDestinations(cms.sections.popular_destinations) : POPULAR_DESTINATIONS;
  const featuredHeading = sectionHeading(cms.sections, 'best_deals', { pre: 'Today’s ', accent: 'SuiteScanner+', rest: ' prices', subtitle: 'Handpicked stays at great prices.' });
  const bestValueHeading = sectionHeading(cms.sections, 'best_value_hotels', { pre: 'Best value ', accent: 'hotel offers', rest: '', subtitle: 'Highly rated stays under US$200/night.' });
  const luxuryHeading = sectionHeading(cms.sections, 'hot_picks', { pre: 'Hotels defined by ', accent: 'luxury', rest: '', subtitle: 'Signature five-star stays and standout properties.' });
  const destinationsHeading = sectionHeading(cms.sections, 'popular_destinations', { pre: 'Popular ', accent: 'destinations', rest: '', subtitle: 'Start with a city and explore the best collection of hotels and stays.' });

  // Counted once the rail shows CMS content (or its fallback, if the CMS is down).
  useEffect(() => {
    if (cms.status !== 'loading' && featuredOffers.length > 0) {
      track('cms.section_viewed', { sectionKey: 'todays_prices', itemCount: featuredOffers.length });
    }
  }, [cms.status, featuredOffers.length]);

  function occupancyParam(): string {
    if (search.guests.occupancy?.length) return encodeOccupancy(search.guests.occupancy);
    const rooms = Math.max(1, Math.min(3, search.guests.rooms));
    const adultsPerRoom = Math.max(1, Math.floor(search.guests.adults / rooms));
    const occupancy: Occupancy = Array.from({ length: rooms }, (_, index) => ({
      adults: adultsPerRoom + (index < search.guests.adults % rooms ? 1 : 0),
      children: index === 0 ? Array.from({ length: search.guests.children }, () => 8) : [],
    }));
    return encodeOccupancy(occupancy);
  }

  function handleSelectPlace(place: Place, currentSearch: SearchBarValue) {
    setSearch(currentSearch);
  }

  async function searchDestination(destination: string, selectedPlace?: Place | null) {
    const common = {
      checkin: search.dates.start ?? '',
      checkout: search.dates.end ?? '',
      residency: search.residency,
      occ: occupancyParam(),
    };

    if (selectedPlace?.kind === 'hotel') {
      const hid = selectedPlace.hid ?? (selectedPlace.id ? Number(selectedPlace.id) : null);
      if (hid) {
        router.push({ pathname: '/hotel/[id]', params: { ...common, id: String(hid), source: 'direct' } });
        return;
      }
    }

    if (selectedPlace?.kind === 'city') {
      const regionId = selectedPlace.id;
      router.push({
        pathname: '/search',
        params: {
          ...common,
          destination: selectedPlace.label,
          region: selectedPlace.label,
          ...(regionId ? { regionId: String(regionId) } : {}),
        },
      });
      return;
    }

    const matchedHotel = findHotelPlace(destination);
    if (matchedHotel?.hid) {
      router.push({ pathname: '/hotel/[id]', params: { ...common, id: String(matchedHotel.hid), source: 'direct' } });
      return;
    }

    try {
      const response = await api.search.destinations({ query: destination });
      const hotel = response.data?.hotels?.[0];
      const region = response.data?.regions?.[0];

      const exactHotel = exactHotelMatch(response.data, destination);
      if (exactHotel) {
        router.push({ pathname: '/hotel/[id]', params: { ...common, id: String(exactHotel.hid), source: 'direct' } });
        return;
      }

      const resolvedRegionId = region?.regionId ?? hotel?.regionId;
      router.push({
        pathname: '/search',
        params: {
          ...common,
          destination,
          region: region?.name ?? destination,
          ...(resolvedRegionId ? { regionId: String(resolvedRegionId) } : {}),
        },
      });
    } catch {
      router.push({
        pathname: '/search',
        params: {
          ...common,
          destination,
          region: destination,
        },
      });
    }
  }

  function handleSearch() {
    if (productMode !== 'hotels') return;
    void searchDestination(search.destination, search.selectedDestination);
  }

  // A CMS card without a bookable hotel id searches its destination instead.
  function hotelHref(hotel: HotelSummary | CmsHotel): string | undefined {
    if (!hotel.hid) return undefined;
    const dealSearch = 'dealSearch' in hotel ? hotel.dealSearch : null;
    return buildHotelHref({
      id: hotel.hid,
      checkin: dealSearch?.checkin ?? search.dates.start,
      checkout: dealSearch?.checkout ?? search.dates.end,
      occ: dealSearch?.occ ?? occupancyParam(),
      residency: dealSearch?.residency ?? search.residency,
    });
  }

  function goToHotel(hotel: HotelSummary | CmsHotel, section?: { key: string; position: number }) {
    if (section) {
      track('cms.item_clicked', { sectionKey: section.key, hid: hotel.hid, position: section.position });
    }
    if (!hotel.hid) {
      if ('destination' in hotel) void searchDestination(hotel.destination.label, hotel.destination.place);
      return;
    }
    const dealSearch = 'dealSearch' in hotel ? hotel.dealSearch : null;
    const common = dealSearch ?? {
      checkin: search.dates.start ?? '',
      checkout: search.dates.end ?? '',
      residency: search.residency,
      occ: occupancyParam(),
    };
    router.push({ pathname: '/hotel/[id]', params: { ...common, id: String(hotel.hid), source: section ? 'cms' : 'direct' } });
  }

  function handleRailScroll(event: NativeSyntheticEvent<NativeScrollEvent>) {
    setRailX(event.nativeEvent.contentOffset.x);
  }

  function scrollRail(direction: -1 | 1) {
    const step = Math.max(280, railViewportWidth * 0.82);
    const maxX = Math.max(0, railContentWidth - railViewportWidth);
    const nextX = Math.min(maxX, Math.max(0, railX + direction * step));
    setRailX(nextX);
    railRef.current?.scrollTo({ x: nextX, animated: true });
  }

  const canScrollLeft = railX > 8;
  const canScrollRight = railX + railViewportWidth < railContentWidth - 8;

  const faqHalf = Math.ceil(HOMEPAGE_FAQS.length / 2);
  const activeProduct = PRODUCT_MODES.find((mode) => mode.id === productMode) ?? PRODUCT_MODES[0];
  const homepageCategories = (
    <View style={styles.navCategories}>
      {VISIBLE_PRODUCT_MODES.map((mode) => {
        const active = mode.id === productMode;
        const navCondensed = width < 560;
        const icon = mode.id === 'hotels' ? 'bed-outline' : mode.id === 'flights' ? 'airplane-outline' : 'car-outline';
        return (
          <Pressable
            key={mode.id}
            accessibilityRole="button"
            accessibilityState={{ selected: active, disabled: !mode.available }}
            disabled={!mode.available}
            onPress={() => setProductMode(mode.id)}
            style={({ pressed }) => [
              styles.navCategory,
              navCondensed && styles.navCategoryCondensed,
              active && styles.navCategoryActive,
              pressed && mode.available && styles.navCategoryPressed,
            ]}
          >
            <Ionicons name={icon} size={navCondensed ? 15 : 16} color={active ? colors.brand : colors.inkFaint} />
            <View style={styles.navCategoryCopy}>
              <Text style={[styles.navCategoryLabel, navCondensed && styles.navCategoryLabelCondensed, active && styles.navCategoryLabelActive]}>{mode.label}</Text>
              {!mode.available ? <Text style={styles.navCategorySoon}>Coming soon</Text> : null}
            </View>
          </Pressable>
        );
      })}
    </View>
  );

  const searchQuickFilters = (
    <View style={[styles.quickFilters, width < 768 && styles.quickFiltersCompact, width <= 340 && styles.quickFiltersNarrow]}>
      <Checkbox label="Free cancellation" checked={freeCancellationOnly} onChange={setFreeCancellationOnly} compact={width < 768} />
      <Checkbox label="4 stars +" checked={fourStarsPlus} onChange={setFourStarsPlus} compact={width < 768} />
    </View>
  );

  return (
    <Screen>
      <Head>
        <title>SuiteScanner — Direct hotel booking, no commission markup</title>
        <meta
          name="description"
          content="Book hotels direct at the price without the middleman markup. Same hotel, same room, lower price — plus a member-only SuiteScanner+ rate when you sign in."
        />
      </Head>
      <TopNav afterBrand={homepageCategories} />

      <View style={styles.heroIntro}>
        <View style={styles.heroIntroInner}>
          <View style={[styles.promiseRow, heroStacked && styles.promiseRowStacked]}>
            <View style={[styles.heroIcon, heroStacked && styles.heroIconStacked]}>
              <Ionicons
                name="bed-outline"
                size={heroStacked ? 36 : 46}
                color={colors.brand}
                style={[styles.heroBedIcon, heroStacked && styles.heroBedIconStacked]}
              />
            </View>
            <View style={styles.heroCopy}>
              <Text style={[styles.heroTitle, heroStacked && styles.heroTitleStacked]}>Book like an insider.</Text>
            </View>
          </View>
        </View>
      </View>

      {cms.status === 'loading' ? (
        <HotelRailSkeleton featured />
      ) : featuredOffers.length > 0 ? (
      <View style={styles.offerBand}>
        <View style={styles.offerInner}>
          <View style={styles.offerHeader}>
            <View style={styles.offerHeaderCopy}>
              <SplitHeading pre={featuredHeading.pre} accent={featuredHeading.accent} rest={featuredHeading.rest} />
              {featuredHeading.subtitle ? <Text style={styles.offerSubtitle}>{featuredHeading.subtitle}</Text> : null}
            </View>
          </View>

          <View
            style={styles.railViewport}
            onLayout={(event) => setRailViewportWidth(event.nativeEvent.layout.width)}
          >
            {!heroStacked ? (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Scroll offers left"
                disabled={!canScrollLeft}
                onPress={() => scrollRail(-1)}
                style={[styles.railButton, styles.railButtonLeft, !canScrollLeft && styles.railButtonDisabled]}
              >
                <Ionicons name="chevron-back" size={22} color={colors.ink} />
              </Pressable>
            ) : null}

            <ScrollView
              ref={railRef}
              horizontal
              showsHorizontalScrollIndicator={false}
              onScroll={handleRailScroll}
              scrollEventThrottle={16}
              onContentSizeChange={(contentWidth) => setRailContentWidth(contentWidth)}
              style={styles.railScroller}
              contentContainerStyle={[
                styles.railContent,
                { paddingLeft: railInset, paddingRight: spacing(3) },
              ]}
              // Free-scrolling with no snap point left a manual swipe landing
              // mid-card (~80% visible) — snap to exactly one card width
              // (+ its gap) so a swipe always settles on a full card, same as
              // the arrow buttons already do via scrollRail's own step.
              snapToInterval={offerCardWidth + space.component}
              snapToAlignment="start"
              decelerationRate="fast"
            >
              {featuredOffers.map((hotel, index) => (
                <SelectRateCard
                  key={hotel.id}
                  hotel={hotel}
                  cardWidth={offerCardWidth}
                  compact={heroStacked}
                  href={hotelHref(hotel)}
                  onPress={() => goToHotel(hotel, { key: 'todays_prices', position: index })}
                />
              ))}
            </ScrollView>

            {!heroStacked ? (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Scroll offers right"
                disabled={!canScrollRight}
                onPress={() => scrollRail(1)}
                style={[styles.railButton, styles.railButtonRight, !canScrollRight && styles.railButtonDisabled]}
              >
                <Ionicons name="chevron-forward" size={22} color={colors.ink} />
              </Pressable>
            ) : null}
          </View>
        </View>
      </View>
      ) : null}

      <View style={styles.searchSection}>
        {/* Blush band continues the pink offers section down behind the heading
            and the top half of the search box; its straight bottom edge sits at
            the box's vertical middle, so the box overlaps blush above and the
            lighter page below. */}
        {searchBoxBox.height > 0 ? (
          <View pointerEvents="none" style={[styles.searchBlush, { height: searchBoxBox.y + searchBoxBox.height / 2 }]} />
        ) : null}
        <View style={styles.searchInner}>
          <View style={[styles.searchHeading, width < 768 && styles.searchHeadingCompact]}>
            {productMode === 'hotels' ? (
              <>
                <SplitHeading pre="Find the " accent="best price" rest=" on your next stay" />
                <Text style={[styles.searchSubtitle, width < 768 && styles.searchSubtitleCompact]}>Search hotels with lower member prices, clearly shown.</Text>
              </>
            ) : (
              <SplitHeading pre="Find your next " accent={productMode === 'flights' ? 'flight' : 'car'} />
            )}
          </View>
          {activeProduct.available ? (
            // White, elevated card — the price proof above already made the
            // case, so the search module itself stays calm and premium rather
            // than competing with it in brand blue.
            <View style={width >= 1024 ? { paddingBottom: 28 } : undefined} onLayout={(event) => setSearchBoxBox({ y: event.nativeEvent.layout.y, height: event.nativeEvent.layout.height })}>
              <ReferenceSearchBar
                value={search}
                onChange={setSearch}
                onSearch={handleSearch}
                onSelectPlace={handleSelectPlace}
                buttonLabel="Search hotels"
                splitDates
                elevated
                richFields
                footer={searchQuickFilters}
              />
            </View>
          ) : (
            <Pressable style={styles.mainSearchComingSoon}>
              <Text style={styles.mainSearchComingSoonText}>{activeProduct.label} are coming soon</Text>
            </Pressable>
          )}

          <View style={styles.recent}>
            <Text style={styles.recentLabel}>Popular searches</Text>
            {RECENT_SEARCHES.map((city) => (
              <RecentChip key={city} city={city} compact={width < 768} onPress={() => searchDestination(city)} />
            ))}
          </View>
        </View>
      </View>

      <View nativeID="why-prices-are-lower" style={styles.pillarBand}>
        <View style={styles.pillarInner}>
          <SplitHeading pre="Why " accent="prices" rest=" are lower" />
          <View style={styles.pillarRow}>
            {RATE_PILLARS.map((pillar) => (
              <View key={pillar.title} style={styles.pillarCard}>
                <Ionicons name={pillar.icon} size={22} color={colors.brand} />
                <Text style={styles.pillarTitle}>{pillar.title}</Text>
                <Text style={styles.pillarBody}>{pillar.body}</Text>
              </View>
            ))}
          </View>
        </View>
      </View>

      {cms.status === 'loading' ? (
        <HotelRailSkeleton />
      ) : bestValueHotels.length > 0 ? (
        <HotelCollectionStrip
          sectionKey="best_value_hotels"
          pre={bestValueHeading.pre}
          accent={bestValueHeading.accent}
          rest={bestValueHeading.rest}
          subtitle={bestValueHeading.subtitle}
          hotels={bestValueHotels}
          onHotelPress={(hotel, index) => goToHotel(hotel, { key: 'best_value_hotels', position: index })}
          getHotelHref={hotelHref}
        />
      ) : null}

      {cms.status === 'loading' ? (
        <HotelRailSkeleton tinted />
      ) : luxuryHotels.length > 0 ? (
        <HotelCollectionStrip
          sectionKey="luxury_hotels"
          pre={luxuryHeading.pre}
          accent={luxuryHeading.accent}
          rest={luxuryHeading.rest}
          subtitle={luxuryHeading.subtitle}
          hotels={luxuryHotels}
          onHotelPress={(hotel, index) => goToHotel(hotel, { key: 'luxury_hotels', position: index })}
          getHotelHref={hotelHref}
          tinted
        />
      ) : null}

      {popularDestinations.length > 0 ? (
      <View style={styles.destinationsSection}>
        <View style={styles.destinationsHeader}>
          <SplitHeading pre={destinationsHeading.pre} accent={destinationsHeading.accent} rest={destinationsHeading.rest} />
          {destinationsHeading.subtitle ? <Text style={styles.destinationsIntro}>{destinationsHeading.subtitle}</Text> : null}
        </View>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.destinationsScroller}
          contentContainerStyle={styles.destinationsRow}
        >
          {popularDestinations.map((destination) => (
            <DestinationCard
              key={destination.key}
              city={destination.city}
              image={destination.image}
              href={`/search?destination=${encodeURIComponent(destination.city)}${destination.place?.id ? `&regionId=${encodeURIComponent(destination.place.id)}` : ''}&checkin=${encodeURIComponent(search.dates.start ?? '')}&checkout=${encodeURIComponent(search.dates.end ?? '')}&occ=${encodeURIComponent(occupancyParam())}&residency=${getDefaultResidency()}`}
              onPress={() => searchDestination(destination.city, destination.place)}
            />
          ))}
        </ScrollView>
      </View>
      ) : null}

      <ComingSoonStrip />

      <View style={styles.faqSection}>
        <SplitHeading pre="Frequently asked " accent="questions" />
        <View style={[styles.faqColumns, heroStacked && styles.faqColumnsStacked]}>
          <View style={[styles.faqCol, heroStacked && styles.faqColStacked]}>
            <Accordion items={HOMEPAGE_FAQS.slice(0, faqHalf)} openQuestion={openFaqQuestion} onToggle={toggleFaqQuestion} />
          </View>
          <View style={[styles.faqCol, heroStacked && styles.faqColStacked]}>
            <Accordion items={HOMEPAGE_FAQS.slice(faqHalf)} openQuestion={openFaqQuestion} onToggle={toggleFaqQuestion} />
          </View>
        </View>
        <Link href="/help" asChild>
          <Pressable accessibilityRole="link" onPress={() => router.push('/help')} style={styles.faqCta}>
            <Text style={styles.faqCtaText}>Visit Help Centre</Text>
            <Ionicons name="arrow-forward" size={15} color={colors.brand} />
          </Pressable>
        </Link>
      </View>

    </Screen>
  );
}

function HotelRailSkeleton({ featured = false, tinted = false }: { featured?: boolean; tinted?: boolean }) {
  return (
    <View style={[featured ? styles.offerBand : styles.collectionSection, tinted && styles.collectionSectionTinted]} accessibilityLabel="Loading hotel offers">
      <View style={featured ? styles.skeletonOfferInner : styles.collectionInner}>
        <View style={styles.skeletonHeading}>
          <SkeletonBlock style={styles.skeletonHeadingLine} />
          <SkeletonBlock style={styles.skeletonSubtitleLine} />
        </View>
        <View style={styles.skeletonRail}>
          {Array.from({ length: 3 }).map((_, index) => (
            <View key={index} style={[styles.skeletonHotelCard, featured && styles.skeletonFeaturedCard]}>
              <SkeletonBlock style={styles.skeletonHotelImage} />
              <SkeletonBlock style={styles.skeletonHotelName} />
              <SkeletonBlock style={styles.skeletonHotelMeta} />
              <SkeletonBlock style={styles.skeletonHotelPrice} />
            </View>
          ))}
        </View>
      </View>
    </View>
  );
}

function HotelCollectionStrip({
  sectionKey,
  pre,
  accent,
  rest,
  subtitle,
  hotels,
  tinted = false,
  onHotelPress,
  getHotelHref,
}: {
  sectionKey: string;
  pre: string;
  accent: string;
  rest?: string;
  subtitle?: string;
  hotels: HotelSummary[];
  tinted?: boolean;
  onHotelPress: (hotel: HotelSummary, index: number) => void;
  getHotelHref?: (hotel: HotelSummary) => string | undefined;
}) {
  const { width } = useWindowDimensions();
  const compact = width < 700;
  const inset = compact ? spacing(3) : Math.max(spacing(3), (width - maxContentWidth) / 2 + spacing(3));
  const scrollRef = useRef<ScrollView>(null);
  const [scrollX, setScrollX] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const canScrollLeft = scrollX > 8;
  const canScrollRight = scrollX + viewportWidth < contentWidth - 8;
  const hotelCount = hotels.length;

  useEffect(() => {
    if (hotelCount > 0) {
      track('cms.section_viewed', { sectionKey, itemCount: hotelCount });
    }
  }, [sectionKey, hotelCount]);

  function scroll(direction: -1 | 1) {
    const next = Math.min(
      Math.max(0, contentWidth - viewportWidth),
      Math.max(0, scrollX + direction * Math.max(280, viewportWidth * 0.72)),
    );
    setScrollX(next);
    scrollRef.current?.scrollTo({ x: next, animated: true });
  }

  return (
    <View style={[styles.collectionSection, tinted && styles.collectionSectionTinted]}>
      <View style={styles.collectionInner}>
        <View style={styles.collectionHeading}>
          <SplitHeading pre={pre} accent={accent} rest={rest} />
          {subtitle ? <Text style={styles.collectionSubtitle}>{subtitle}</Text> : null}
        </View>
        <View style={styles.collectionViewport} onLayout={(event) => setViewportWidth(event.nativeEvent.layout.width)}>
          <ScrollView
            ref={scrollRef}
            horizontal
            showsHorizontalScrollIndicator={false}
            onScroll={(event) => setScrollX(event.nativeEvent.contentOffset.x)}
            scrollEventThrottle={16}
            onContentSizeChange={(nextWidth) => setContentWidth(nextWidth)}
            style={styles.collectionScroller}
            contentContainerStyle={[styles.collectionRow, { paddingLeft: inset, paddingRight: spacing(3) }]}
          >
            {hotels.map((hotel, index) => (
              <CollectionHotelCard
                key={hotel.id}
                hotel={hotel}
                onPress={() => onHotelPress(hotel, index)}
                href={getHotelHref?.(hotel)}
              />
            ))}
          </ScrollView>
          {!compact && canScrollLeft ? (
            <Pressable accessibilityRole="button" accessibilityLabel="Scroll hotels left" onPress={() => scroll(-1)} style={[styles.collectionRailButton, styles.collectionRailButtonLeft]}>
              <Ionicons name="chevron-back" size={21} color={colors.ink} />
            </Pressable>
          ) : null}
          {!compact && canScrollRight ? (
            <Pressable accessibilityRole="button" accessibilityLabel="Scroll hotels right" onPress={() => scroll(1)} style={[styles.collectionRailButton, styles.collectionRailButtonRight]}>
              <Ionicons name="chevron-forward" size={21} color={colors.ink} />
            </Pressable>
          ) : null}
        </View>
      </View>
    </View>
  );
}

function DestinationCard({
  city,
  image,
  onPress,
  href,
}: {
  city: string;
  image: ImageSource | null;
  onPress: () => void;
  href?: string;
}) {
  const cardContent = (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`See hotels in ${city}`}
      onPress={onPress}
      style={styles.destinationCard}
    >
      <View style={styles.destinationImageWrap}>
        {image ? (
          <Image source={image} style={styles.destinationImage} resizeMode="cover" />
        ) : (
          <View style={styles.destinationImage} />
        )}
      </View>
      <View style={styles.destinationLabelRow}>
        <Text style={styles.destinationLabel}>{city}</Text>
      </View>
    </Pressable>
  );

  return href ? (
    <Link href={href as any} asChild>
      {cardContent}
    </Link>
  ) : (
    cardContent
  );
}

function RecentChip({ city, compact, onPress }: { city: string; compact: boolean; onPress: () => void }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`Search ${city}`}
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={[styles.recentChip, compact && styles.recentChipCompact, hovered && styles.recentChipHover]}
    >
      <Ionicons name="search" size={13} color={colors.inkFaint} />
      <Text style={[styles.recentChipText, compact && styles.recentChipTextCompact]}>{city}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  navCategories: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.related,
  },
  navCategory: {
    minHeight: 36,
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.related,
    paddingHorizontal: space.compact,
    borderRadius: radii.full,
    flexShrink: 0,
    ...({ whiteSpace: 'nowrap' } as object),
  },
  navCategoryActive: {
    backgroundColor: colors.skyTint,
  },
  navCategoryCondensed: {
    minHeight: 32,
    paddingHorizontal: space.related,
  },
  navCategoryPressed: {
    opacity: 0.72,
  },
  navCategoryLabel: {
    fontFamily: fontFamily.medium,
    fontSize: 14,
    lineHeight: 16,
    color: colors.inkSoft,
  },
  navCategoryCopy: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
    flexWrap: 'nowrap',
    ...({ whiteSpace: 'nowrap' } as object),
  },
  navCategoryLabelActive: {
    fontFamily: fontFamily.semibold,
    color: colors.brand,
  },
  navCategoryLabelCondensed: {
    fontSize: type.metadata.fontSize,
    lineHeight: type.metadata.lineHeight,
  },
  navCategorySoon: {
    fontFamily: fontFamily.regular,
    fontSize: type.metadata.fontSize,
    lineHeight: 14,
    color: colors.inkFaint,
    flexShrink: 0,
  },
  heroIntro: {
    backgroundColor: colors.card,
  },
  heroIntroInner: {
    maxWidth: maxContentWidth,
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: space.card,
    paddingTop: spacing(3.5),
    paddingBottom: spacing(3.5),
  },
  promiseRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(2),
  },
  heroIcon: {
    width: 48,
    height: 48,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroIconStacked: {
    width: 40,
    height: 40,
  },
  heroBedIcon: {
    lineHeight: 48,
  },
  heroBedIconStacked: {
    lineHeight: 40,
  },
  promiseRowStacked: {
    alignItems: 'flex-start',
    gap: space.compact,
  },
  heroCopy: {
    flex: 1,
    minWidth: 0,
  },
  heroTitle: {
    fontFamily: fontFamily.bold,
    ...type.display,
    letterSpacing: -1,
    color: colors.ink,
  },
  heroTitleStacked: {
    ...typeCompact.display,
    letterSpacing: -0.55,
  },
  heroSubtitle: {
    fontFamily: fontFamily.regular,
    ...type.subsectionTitle,
    color: colors.inkSoft,
    marginTop: space.related,
  },
  offerBand: {
    backgroundColor: '#FFF1F7',
    paddingTop: space.card,
    paddingBottom: space.card,
  },
  offerInner: {
    width: '100%',
    alignSelf: 'center',
  },
  offerHeader: {
    maxWidth: maxContentWidth,
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: spacing(3),
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: space.card,
    marginBottom: space.internal,
  },
  offerHeaderStacked: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: spacing(1.5),
  },
  offerHeaderCopy: {
    flexShrink: 1,
    gap: space.related,
  },
  offerSubtitle: {
    fontFamily: fontFamily.regular,
    ...type.bodyRegular,
    color: colors.inkSoft,
  },
  railViewport: {
    position: 'relative',
  },
  railScroller: {
    width: '100%',
  },
  railContent: {
    alignItems: 'flex-start',
    gap: space.component,
    paddingVertical: space.related,
  },
  railButton: {
    position: 'absolute',
    top: '44%',
    zIndex: 20,
    width: 42,
    height: 42,
    borderRadius: radii.full,
    backgroundColor: colors.white,
    alignItems: 'center',
    justifyContent: 'center',
    ...shadows.card,
  },
  railButtonLeft: {
    left: spacing(2),
  },
  railButtonRight: {
    right: spacing(2),
  },
  railButtonDisabled: {
    opacity: 0,
  },
  searchSection: {
    backgroundColor: colors.cream,
    position: 'relative',
    zIndex: 50,
    marginTop: 0,
  },
  mainSearchComingSoon: {
    minHeight: 88,
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.lineSoft,
    borderRadius: radii.lg,
    backgroundColor: colors.card,
    paddingHorizontal: spacing(2.5),
    ...shadows.card,
  },
  mainSearchComingSoonText: {
    fontFamily: fontFamily.bold,
    fontSize: type.cardTitle.fontSize,
    color: colors.ink,
  },
  searchInner: {
    maxWidth: maxContentWidth,
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: space.card,
    // Generous — this section should feel designed and roomy, not squeezed
    // up against the price strip above it.
    paddingTop: space.cardLarge,
    paddingBottom: space.section,
    gap: 0,
    // Above the blush band so the heading and box paint over it.
    position: 'relative',
    zIndex: 1,
  },
  searchHeading: {
    gap: 8,
    marginBottom: 28,
  },
  searchHeadingCompact: {
    marginBottom: 24,
  },
  searchSubtitle: {
    fontFamily: fontFamily.regular,
    fontSize: 17,
    lineHeight: 26,
    color: colors.inkSoft,
  },
  searchSubtitleCompact: {
    fontSize: 14,
    lineHeight: 21,
  },
  // Full-bleed blush band (same tone as the offers section above), straight
  // bottom edge; height is set inline to the box's vertical middle.
  searchBlush: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: '#FFF1F7',
  },
  quickFilters: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 24,
  },
  quickFiltersCompact: {
    gap: 20,
  },
  quickFiltersNarrow: {
    gap: 16,
  },
  recent: {
    marginTop: 24,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: 8,
    rowGap: 8,
  },
  recentLabel: {
    fontFamily: fontFamily.medium,
    fontSize: type.bodyRegular.fontSize,
    color: colors.inkFaint,
    marginRight: space.micro,
  },
  recentChip: {
    height: 36,
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.related,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.full,
    backgroundColor: colors.card,
    paddingHorizontal: 14,
  },
  recentChipCompact: {
    height: 34,
    paddingHorizontal: 12,
  },
  recentChipHover: {
    borderColor: colors.brand,
  },
  recentChipText: {
    fontFamily: fontFamily.medium,
    fontSize: type.bodyRegular.fontSize,
    color: colors.ink,
  },
  recentChipTextCompact: {
    fontSize: 13,
  },
  pillarBand: {
    backgroundColor: colors.cream,
  },
  pillarInner: {
    maxWidth: maxContentWidth,
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: space.card,
    paddingVertical: space.cardLarge,
    gap: space.card,
  },
  pillarRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: space.cardLarge,
  },
  pillarCard: {
    flexGrow: 1,
    flexBasis: 240,
    maxWidth: 360,
    gap: space.related,
  },
  pillarTitle: {
    fontFamily: fontFamily.semibold,
    ...type.bodyLarge,
    color: colors.ink,
    marginTop: space.micro,
  },
  pillarBody: {
    fontFamily: fontFamily.regular,
    ...type.bodyRegular,
    color: colors.inkSoft,
  },
  collectionSection: {
    backgroundColor: colors.cream,
    paddingVertical: space.cardLarge,
  },
  collectionSectionTinted: {
    backgroundColor: '#FFF1F7',
  },
  skeletonOfferInner: { width: '100%', maxWidth: maxContentWidth, alignSelf: 'center', paddingHorizontal: spacing(3), paddingVertical: spacing(4), gap: spacing(3) },
  skeletonHeading: { gap: spacing(1), paddingHorizontal: spacing(3), maxWidth: 520 },
  skeletonHeadingLine: { width: 300, maxWidth: '80%', height: 25 },
  skeletonSubtitleLine: { width: 240, maxWidth: '65%', height: 14 },
  skeletonRail: { flexDirection: 'row', gap: space.component, paddingHorizontal: spacing(3), overflow: 'hidden' },
  skeletonHotelCard: { width: 320, backgroundColor: colors.card, borderWidth: 1, borderColor: colors.lineSoft, borderRadius: radii.lg, padding: spacing(1.5), gap: spacing(1.25) },
  skeletonFeaturedCard: { width: 359 },
  skeletonHotelImage: { width: '100%', height: 170, borderRadius: radii.md },
  skeletonHotelName: { width: '76%', height: 18 },
  skeletonHotelMeta: { width: '48%', height: 12 },
  skeletonHotelPrice: { width: '35%', height: 24, marginTop: spacing(1) },
  collectionInner: {
    width: '100%',
    alignSelf: 'center',
    gap: space.card,
  },
  collectionHeading: {
    maxWidth: maxContentWidth,
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: space.card,
  },
  collectionViewport: {
    position: 'relative',
    width: '100%',
  },
  collectionScroller: {
    width: '100%',
  },
  collectionRow: {
    gap: space.component,
  },
  collectionRailButton: {
    position: 'absolute',
    top: 82,
    zIndex: 10,
    width: 42,
    height: 42,
    borderRadius: radii.full,
    backgroundColor: colors.white,
    alignItems: 'center',
    justifyContent: 'center',
    ...shadows.card,
  },
  collectionRailButtonLeft: { left: spacing(2) },
  collectionRailButtonRight: { right: spacing(2) },
  collectionSubtitle: {
    marginTop: space.related,
    fontFamily: fontFamily.regular,
    ...type.bodyRegular,
    color: colors.inkSoft,
  },
  // Dark muted pill, white text — location over the top-left of the image.
  // Every row below is fixed-height so names, ratings, prices and CTAs line up
  // perfectly across the carousel regardless of hotel-name length.
  destinationsSection: {
    maxWidth: maxContentWidth,
    width: '100%',
    alignSelf: 'center',
    paddingVertical: space.cardLarge,
    gap: space.card,
  },
  destinationsHeader: {
    paddingHorizontal: space.card,
    gap: space.related,
  },
  destinationsIntro: {
    fontFamily: fontFamily.regular,
    ...type.bodyRegular,
    color: colors.inkSoft,
  },
  destinationsScroller: {
    width: '100%',
  },
  destinationsRow: {
    paddingHorizontal: space.card,
    gap: space.internal,
  },
  destinationCard: {
    width: 268,
    gap: space.compact,
  },
  // A separate clipping wrapper (rather than the radius living on Image
  // itself) so the corners stay crisp under the card's hover scale transform.
  destinationImageWrap: {
    width: '100%',
    height: 190,
    borderRadius: radii.lg,
    overflow: 'hidden',
    backgroundColor: colors.lineSoft,
  },
  destinationImage: {
    width: '100%',
    height: '100%',
  },
  destinationLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  destinationLabel: {
    fontFamily: fontFamily.semibold,
    ...type.cardTitle,
    color: colors.ink,
  },
  faqSection: {
    maxWidth: maxContentWidth,
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: space.card,
    paddingVertical: space.cardLarge,
    gap: space.card,
  },
  faqColumns: {
    flexDirection: 'row',
    gap: space.cardLarge,
    alignItems: 'flex-start',
  },
  faqColumnsStacked: {
    flexDirection: 'column',
    gap: 0,
  },
  faqCol: {
    flexGrow: 1,
    flexBasis: 0,
    minWidth: 0,
  },
  faqColStacked: {
    width: '100%',
    flexGrow: 0,
    flexBasis: 'auto',
  },
  faqCta: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: space.micro,
  },
  faqCtaText: {
    fontFamily: fontFamily.semibold,
    ...control.secondaryCta,
    color: colors.brand,
  },
});
